// Shared Stream / first-party messenger body for Firefly Messages (+ legacy hosts).
import { useEffect } from "react";
import { useWorkplaceStreamChatSession } from "@/contexts/WorkplaceStreamChatSessionContext";
import { WorkplaceVendorChatPanel } from "@/components/messages/WorkplaceVendorChatPanel";
import {
  useWorkplaceMessagesState,
  type UseWorkplaceMessagesStateResult,
} from "@/components/messages/useWorkplaceMessagesState";
import { WorkplaceMessagesPanel } from "@/components/messages/WorkplaceMessagesPanel";
import { WorkplaceMessengerToolbar } from "@/components/messages/WorkplaceMessengerToolbar";
import type { StreamChatLayout } from "@/components/messages/streamChatLayout";

export type WorkplaceMessengerBodyProps = {
  /** When true, refresh first-party member/thread lists (popover open / dock expanded). */
  active: boolean;
  /** Compact density for the Firefly dock card. */
  compact?: boolean;
  /** Stream pane size relative to host chrome (default contracted). */
  layout?: StreamChatLayout;
};

export function WorkplaceMessengerBody({
  active,
  compact = false,
  layout,
}: WorkplaceMessengerBodyProps) {
  const session = useWorkplaceStreamChatSession();
  const wpState = useWorkplaceMessagesState();
  const streamLayout: StreamChatLayout = layout ?? (compact ? "compact" : "contracted");
  const toolbarCompact = compact || streamLayout === "compact";

  useEffect(() => {
    if (active) wpState.loadOnOpen();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active]);

  const showVendor =
    session.vendorEnabled === true &&
    session.ready &&
    !session.useFallback &&
    Boolean(session.tokenPayload) &&
    Boolean(session.client);

  const rootClass =
    streamLayout === "compact"
      ? "space-y-2"
      : "flex h-full min-h-0 min-w-0 w-full flex-col gap-2";

  if (session.vendorEnabled === null || (session.loading && !showVendor)) {
    return <p className="text-xs text-gray-500">Connecting messenger…</p>;
  }
  if (session.vendorEnabled === false) {
    return (
      <p className={`text-gray-600 dark:text-gray-300 ${compact ? "text-xs" : "text-sm"}`}>
        Team chat is not enabled for this organization yet.
      </p>
    );
  }
  if (showVendor && session.tokenPayload) {
    return (
      <div className={rootClass}>
        <WorkplaceMessengerToolbar
          compact={toolbarCompact}
          orgId={session.tokenPayload.org_id}
          bridgeChannelSlugs={session.bridgeChannelSlugs}
          mutedChannelIds={session.mutedChannelIds}
          onBridgeMute={session.setBridgeMuted}
          hideAlertControls={compact || streamLayout === "compact"}
        />
        <div className={streamLayout === "compact" ? undefined : "min-h-0 min-w-0 flex-1"}>
          <WorkplaceVendorChatPanel
            tokenPayload={session.tokenPayload}
            displayName={session.displayName}
            members={wpState.members}
            allOrgMembers={wpState.allOrgMembers}
            orgChannels={session.orgChannels}
            bridgeChannelSlugs={session.bridgeChannelSlugs}
            mutedChannelIds={session.mutedChannelIds}
            onStartDm={session.startDm}
            onCreateOrgChannel={session.createOrgChannel}
            onDeleteOrgChannel={session.deleteOrgChannel}
            onBridgeMute={session.setBridgeMuted}
            onReload={session.reload}
            layout={streamLayout}
            hideMuteSection
          />
        </div>
      </div>
    );
  }
  return (
    <div className="space-y-2">
      <WorkplaceMessengerToolbar compact={toolbarCompact} />
      <p className="text-[11px] text-amber-700 dark:text-amber-300">
        Stream unavailable
        {session.fallbackReason ? ` (${session.fallbackReason})` : ""}
        {session.errorText ? `: ${session.errorText}` : ""}. Showing first-party messages.
      </p>
      <WorkplaceMessagesPanel {...firstPartyPanelProps(wpState)} />
    </div>
  );
}

function firstPartyPanelProps(wpState: UseWorkplaceMessagesStateResult) {
  return {
    allowTestNotification: false,
    allowSenderLock: false,
    allowRequiresAck: false,
    errorText: wpState.errorText,
    unreadCount: wpState.unreadCount,
    unackedCount: wpState.unackedCount,
    testBusy: wpState.testBusy,
    markBusy: wpState.markBusy,
    replyBusy: wpState.replyBusy,
    archiveBusy: wpState.archiveBusy,
    loadingList: wpState.loadingList,
    threads: wpState.threads,
    selectedThreadId: wpState.selectedThreadId,
    messages: wpState.messages,
    participantReceipts: wpState.participantReceipts,
    members: wpState.members,
    viewer: wpState.viewer,
    recipientId: wpState.recipientId,
    setRecipientId: wpState.setRecipientId,
    newThreadTitle: wpState.newThreadTitle,
    setNewThreadTitle: wpState.setNewThreadTitle,
    newThreadText: wpState.newThreadText,
    setNewThreadText: wpState.setNewThreadText,
    newThreadSenderLocked: wpState.newThreadSenderLocked,
    setNewThreadSenderLocked: wpState.setNewThreadSenderLocked,
    newThreadRequiresAck: wpState.newThreadRequiresAck,
    setNewThreadRequiresAck: wpState.setNewThreadRequiresAck,
    newThreadAckDeadline: wpState.newThreadAckDeadline,
    setNewThreadAckDeadline: wpState.setNewThreadAckDeadline,
    replyText: wpState.replyText,
    setReplyText: wpState.setReplyText,
    replyRequiresAck: wpState.replyRequiresAck,
    setReplyRequiresAck: wpState.setReplyRequiresAck,
    onMarkAll: wpState.onMarkAll,
    onTest: wpState.onTest,
    onCreateThread: wpState.onCreateThread,
    onSendReply: wpState.onSendReply,
    onSelectThread: wpState.onSelectThread,
    onMarkOne: wpState.onMarkOne,
    onAcknowledge: wpState.onAcknowledge,
    onArchiveOne: wpState.onArchiveOne,
    onArchiveThread: wpState.onArchiveThread,
  };
}
